import Button from '@carvana/ds-button';
import React from 'react';
import SomethingWentWrongModal from '|/components/carvana-ds/something-went-wrong-modal/something-went-wrong-modal';
import { Consents } from './consents';
import { DigitalWallets } from './digital-wallets';
import { ImpersonationBlockedBanner } from './impersonation-blocked-banner';
import styles from './pay-at-order.module.css';
import { PaymentDetailsCard } from './payment-details-card';
import { PlaidModal } from './plaid-modal';
import { usePayAtOrder } from './use-pay-at-order';
import type { CompletePurchaseSubmitResult } from '../../use-complete-purchase-vm';

interface PayAtOrderProps {
  onSubmit: () => Promise<CompletePurchaseSubmitResult>;
  editPaymentMethod: () => void;
}

export const PayAtOrder = ({ onSubmit, editPaymentMethod }: PayAtOrderProps) => {
  const {
    paymentSelection,
    requestForPaymentAmount,
    isSubmitting,
    isButtonDisabled,
    isPlaidModalOpen,
    shouldUseDigitalWallet,
    shouldRenderDigitalWallets,
    shouldShowConsents,
    shouldShowImpersonationText,
    shouldInitiateRequestForPayment,
    hasCustomerConfirmedSufficientFunds,
    buttonText,
    setHasCustomerConfirmedSufficientFunds,
    setIsSubmitting,
    setIsDigitalWalletSupported,
    setIsPlaidModalOpen,
    onDigitalWalletPaymentCollected,
    onPurchaseRolledBack,
    handleError,
    handleSubmit,
    onPlaidExit,
    submitPayAtOrder
  } = usePayAtOrder({ onSubmit, editPaymentMethod });

  return (
    <>
      <div className={styles.container}>
        <div className={styles.contentContainer}>
          <PaymentDetailsCard />

          <div className={styles.belowCard}>
            {shouldShowConsents && (
              <Consents
                disableClickOnSufficientFundsCheckbox={isSubmitting}
                hasCustomerConfirmedSufficientFunds={hasCustomerConfirmedSufficientFunds}
                setHasCustomerConfirmedSufficientFunds={setHasCustomerConfirmedSufficientFunds}
              />
            )}

            {shouldShowImpersonationText && (
              <ImpersonationBlockedBanner
                shouldUseDigitalWallet={shouldUseDigitalWallet}
                shouldInitiateRequestForPayment={shouldInitiateRequestForPayment}
                selectionType={paymentSelection?.selectionType}
              />
            )}

            {shouldRenderDigitalWallets && (
              <DigitalWallets
                editPaymentMethod={editPaymentMethod}
                setIsSubmitting={setIsSubmitting}
                onSupportChange={setIsDigitalWalletSupported}
                onPaymentCollected={onDigitalWalletPaymentCollected}
                onPurchaseRolledBack={onPurchaseRolledBack}
                onError={handleError}
              />
            )}

            {!shouldUseDigitalWallet && (
              <div className={styles.buttonContainer}>
                <Button
                  variant="primary"
                  size="md"
                  width="full"
                  disabled={isButtonDisabled}
                  isLoading={isSubmitting}
                  onClick={() => {
                    void submitPayAtOrder();
                  }}
                  data-analytics-id="pp_complete_purchase_pao_submit"
                >
                  {buttonText}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <PlaidModal
        isOpen={isPlaidModalOpen}
        onClose={() => {
          setIsPlaidModalOpen(false);
        }}
        paymentAmount={requestForPaymentAmount ?? 0}
        onSuccess={() => {
          handleSubmit().finally(() => {
            setIsPlaidModalOpen(false);
          });
        }}
        onExit={() => {
          setIsPlaidModalOpen(false);
          onPlaidExit();
        }}
        onError={() => {
          setIsPlaidModalOpen(false);
          handleError();
        }}
      />

      <SomethingWentWrongModal text="collecting your payment" />
    </>
  );
};
